<script lang="ts">
  import type {Poem} from '../../types'
  import api from '../../api/api'
  import {user} from '../../stores/user'
  import slugify from 'slugify'
  import {navigate} from 'svelte5-router'
  import {t} from '../../i18n'
  import PoemEditor from './PoemEditor.svelte'
  import PoemSettings from './PoemSettings.svelte'
  import PoemCover from './PoemCover.svelte'
  import PoemPublish from './PoemPublish.svelte'
  import {titleFromContent} from './utils'
  import {type SvelteComponent} from 'svelte'
  import {flash} from '../../stores/flash'

  export let handle: string | undefined = undefined
  export let step: typeof steps[number] = 'editor'

  const steps = ['edit', 'settings', 'cover', 'publish']
  let poem = {} as Poem
  let childComponent: SvelteComponent

  $: stepIndex = steps.indexOf(step)
  $: if (handle) load(handle)

  async function load(handle: string | undefined) {
    if (!handle) {
      poem = {
        authors: [$user]
      } as any as Poem

      if (step !== 'editor') navigate('/poems/new')
    } else {
      poem = await api.get<Poem>(`/poems/${handle}`)
    }

    checkPermission()
  }

  function checkPermission() {
    if (poem.id && (!$user || $user.id !== poem.user_id)) {
      flash.show(t.flash.page.blank, 'error')
      navigate('/explore')
    }
  }


  function stepTo(e: Event, stepIndex: number): void {
    e.preventDefault()
    // Ensure title is set before validating so required check passes
    if (!poem.title) {
      poem.title = titleFromContent(poem.content)
    }

    if (!validateStep()) return

    if (poem.id) {
      updatePoem(stepIndex)
    } else {
      createPoem(stepIndex)
    }
  }

  function validateStep(): boolean {
    if (childComponent && childComponent.validate) return childComponent.validate()
    return true
  }

  async function updatePoem(nextStepIndex: number) {
    // Generate preview with 250 character limit
    const paragraphs = poem.content.match(/<p>(.*?)<\/p>/g)?.slice(0, 4).join('') || ''
    poem.preview = paragraphs.length > 250 ? paragraphs.substring(0, 250) + '...' : paragraphs
    
    poem = await api.put<Poem>(`/poems/${poem.id}`, {poem})

    if (poem.status === 'published' && step === 'cover') {
      navigate(`/poems/${poem.handle}`)
    } else {
      const path = steps[nextStepIndex] === 'editor' ? 'edit' : steps[nextStepIndex]
      navigate(`/poems/${poem.handle}/${path}`)
    }
  }

  async function createPoem(nextStepIndex: number) {
    poem.handle = slugify(poem.title || titleFromContent(poem.content), {lower: true}).slice(0, 100)
    
    // Generate preview with 250 character limit
    const paragraphs = poem.content.match(/<p>(.*?)<\/p>/g)?.slice(0, 4).join('') || ''
    poem.preview = paragraphs.length > 250 ? paragraphs.substring(0, 250) + '...' : paragraphs

    poem = await api.post<Poem>('/poems', {poem})
    navigate(`/poems/${poem.handle}/${steps[nextStepIndex]}`)
  }

  async function publish(e: Event) {
    e.preventDefault()
    await api.put(`/poems/${poem.id}/publish`)
    navigate(`/poems/${poem.handle}`)
  }

  $: lastStep = stepIndex === steps.length - 1
</script>

<div class="row">
  <div class="add-poem-wrap">
    <div class="add__steps">
      {#each steps as s, index}
        <span class="step step-{index + 1}" class:active={s === step}>
          {#if index >= stepIndex}
            {(t.poem.steps as any)[(index + 1).toString()]}
          {:else}
            <a onclick={(e) => stepTo(e, index)} href="#">
              {(t.poem.steps as any)[(index + 1).toString()]}
            </a>
          {/if}
        </span>
      {/each}
    </div>

    {#if step === 'edit'}
      <PoemEditor bind:poem bind:this={childComponent} {stepTo}/>
    {:else if step === 'settings'}
      <PoemSettings bind:poem bind:this={childComponent} {stepTo}/>
    {:else if step === 'cover'}
      <PoemCover bind:poem bind:this={childComponent} {stepTo}/>
    {:else if step === 'publish'}
      <PoemPublish bind:poem bind:this={childComponent}/>
    {/if}

    <!-- Disclaimer for read-only users -->
    {#if $user && $user.read_only && lastStep}
      <div class="disclaimer-box">
        <h3>{t.poem.disclaimer.title}</h3>
        <div class="disclaimer-content">
          <p>{t.poem.disclaimer.intro}</p>
          <ul>
            <li>{t.poem.disclaimer.rule1}</li>
            <li>{t.poem.disclaimer.rule2}</li>
            <li>{t.poem.disclaimer.rule3}</li>
            <li>{t.poem.disclaimer.rule4}</li>
            <li>{t.poem.disclaimer.rule5}</li>
            <li>{t.poem.disclaimer.rule6}</li>
          </ul>
          <p class="disclaimer-footer">{t.poem.disclaimer.footer}</p>
        </div>
      </div>
    {/if}

    {#if lastStep}
      <div class="next-step-wrap">
        <a class="ui button inverted" onclick={publish}>
          {t.poem.steps.publish}
        </a>
      </div>
    {/if}
  </div>
</div>

